/**
 * Firebase Storage URL Converter
 * Converts gs:// URLs to downloadable https:// URLs
 * @module services/sound/url-converter
 */

/**
 * Convert Firebase Storage gs:// URL to downloadable https:// URL
 *
 * @example
 * gsUrlToHttps('gs://bucket/path/file.mp3')
 * // Returns: 'https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Ffile.mp3?alt=media'
 *
 * @param gsUrl - Firebase Storage URL starting with gs://
 * @returns HTTPS URL that can be used for downloads/playback
 */
export function gsUrlToHttps(gsUrl: string): string {
  // Return unchanged if not a gs:// URL
  if (!gsUrl || !gsUrl.startsWith('gs://')) {
    return gsUrl;
  }

  // Remove 'gs://' prefix
  const withoutProtocol = gsUrl.slice(5);

  // Split into bucket and path
  const slashIndex = withoutProtocol.indexOf('/');
  if (slashIndex === -1) {
    // No path, just bucket
    return gsUrl;
  }

  const bucket = withoutProtocol.slice(0, slashIndex);
  const path = withoutProtocol.slice(slashIndex + 1);

  // URL encode the path (encode slashes as %2F)
  const encodedPath = encodeURIComponent(path);

  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
}

/**
 * Check if a URL is a Firebase Storage gs:// URL
 */
export function isGsUrl(url: string): boolean {
  return url?.startsWith('gs://') ?? false;
}
